<script setup lang="ts">
import Loading from '@/atoms/Loading.vue'
import { ButtonState, ButtonType } from './types'

export interface NitButtonProps {
  type?: ButtonType
  buttonState?: ButtonState
  disabled?: boolean
  size?: 'S' | 'M' | 'L'
  width?: string
  height?: string
  submit?: boolean
}

const props = withDefaults(defineProps<NitButtonProps>(), {
  type: 'main',
  buttonState: 'available',
  disabled: false,
  size: 'M',
  width: '',
  height: '',
  submit: false
})

const { buttonState, type } = toRefs(props)
const isLoading = computed(() => buttonState.value === 'loading')
const isDisabled = computed(() => buttonState.value === 'disabled')
const loadingRectSize = 46
const buttonStyle = computed(() => {
  return {
    '--color': `var(--${type.value})`,
    '--text-color': type.value === 'tertiary' ? '#291b0fcc' : 'white',
    '--hover-color': `var(--${type.value}-hover)`,
    '--active-color': `var(--${type.value}-active)`
  }
})
</script>
<template>
  <button
    class="button-container lo-center nowrap"
    :disabled="isLoading || isDisabled || disabled"
    :class="{
      disabled: isDisabled,
      loading: isLoading,
      S: size === 'S',
      M: size === 'M',
      L: size === 'L',
      [type]: true
    }"
    :style="{ ...buttonStyle, width, height, maxWidth: width }"
    :type="submit ? 'submit' : 'button'"
  >
    <div v-show="!isLoading" class="button-content">
      <slot />
    </div>
    <Loading v-if="isLoading" :rect-size="loadingRectSize" :color="'#FFFFFF'" />
  </button>
</template>

<style scoped>
.S {
  padding: 10px 16px;
  /* 14px-medium */
  font-size: var(--font-size-14px);
  font-weight: var(--font-weight-medium);
  line-height: 1;
}
.M {
  width: 100%;
  max-width: 310px;
  height: 48px;
  padding: 12px 16px;
  font-weight: var(--font-weight-medium);
  line-height: 1;
}
.L {
  width: 100%;
  height: 48px;
  padding: 12px 16px;
  font-weight: var(--font-weight-medium);
  line-height: 1;
}

/* デフォルトのスタイル */
.button-container {
  color: var(--text-color);
  background: var(--color);
  cursor: pointer;
  border-radius: 6px;
}

/* リンクボタン */
.button-container:deep(a) {
  text-decoration: none;
  color: var(--text-color);
}

/* tertiaryだけスタイルが異なる */
.tertiary {
  border: 1px solid var(--border-button);
}

/* Hover */
button:hover:enabled {
  background-color: var(--hover-color);
}
@media screen and (max-width: 800px) {
  /* スマホの場合タップするとhover状態になるため何もしない */
  button:hover:enabled {
    background: var(--color);
  }
}

/* Active */
button:active:enabled {
  background: var(--active-color);
}
button:focus-visible {
  background: var(--active-color);
}

/* Disabled */
.disabled {
  background: var(--disabled) !important;
}
button:disabled {
  background: var(--disabled) !important;
  cursor: not-allowed;
}

/* Loading */
.loading {
  /* Hoverより優先するため */
  background: var(--active-color) !important;
  /* Disabledより優先させるため */
  cursor: progress !important;
}

/* 中身のレイアウト */
.button-content {
  display: flex;
  align-items: center;
  justify-content: center;
}
/* 中身をホバーしたときにポインターが出るようにするため */
.button-content:deep(*) {
  cursor: pointer;
}
</style>
